import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { getSelectedLocationIds } from '@app/data/helpers/locationHelpers';
import { EChartsOption } from 'echarts';
import {
  getRiparianFeaturesChartConfig,
  RiparianFeaturesDataByDate,
  RiparianFeaturesData,
} from './rongo-riparian-features-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown, renderMetricTable } from '@app/widgets/helpers/organisation-breakdown';

@Component({
  selector: 'app-rongo-riparian-features-chart',
  templateUrl: './rongo-riparian-features-chart.component.html',
  styleUrls: ['./rongo-riparian-features-chart.component.scss'],
})
export class RongoRiparianFeaturesChartComponent implements OnInit {
  chartTitle: string = 'Rongo Riparian Features';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-riparian-features';
  startDate: any = '';
  endDate: any = '';
  locationIds: string[] = [];
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<RiparianFeaturesDataByDate>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService
  ) {}

  ngOnInit() {
    this.locationIds = getSelectedLocationIds(this.filterService.getFilters().selectedLocations);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }
  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService.getRiparianFeaturesData(this.startDate, this.endDate, this.locationIds, 'organisation').subscribe(
      (data: RongoReportingData<RiparianFeaturesDataByDate>) => {
        const { config, data: riparianFeaturesData, breakdown } = data;
        this.chartTitle = config.title;
        this.chartSubtitle = config.subTitle || '';
        this.breakdown = breakdown?.groups || null;
        this.noData = Object.values(riparianFeaturesData).every((entry: RiparianFeaturesData) => {
          return (
            entry.riparianCoverageText.value === 0 &&
            entry.taongaSpeciesAbundance.value === 0 &&
            entry.taongaWaiSpeciesAbundance.value === 0 &&
            entry.rongoaSpeciesAbundance.value === 0
          );
        });
        this.chartOptions = withOrganisationBreakdown(
          getRiparianFeaturesChartConfig(riparianFeaturesData),
          this.breakdown,
          {
            buildGroupOption: (d) => getRiparianFeaturesChartConfig(d),
            renderFlat: (groups) =>
              renderMetricTable(groups, [
                { key: 'riparianCoverageText', label: 'Riparian Coverage' },
                { key: 'taongaSpeciesAbundance', label: 'Taonga Abundance' },
                { key: 'taongaWaiSpeciesAbundance', label: 'Taonga Wai Abundance' },
                { key: 'rongoaSpeciesAbundance', label: 'Rongoa Abundance' },
              ]),
          }
        );
      },
      (error: any) => {
        console.error('Error fetching data:', error);
      }
    );
  };
  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };
  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const newLocationIds = getSelectedLocationIds(filters.filters.selectedLocations);
    const locationsChanged = newLocationIds.join(',') !== this.locationIds.join(',');
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || locationsChanged || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.locationIds = newLocationIds;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }
}
